import React, { useState, useEffect, useRef } from 'react'
import { TouchableOpacity, View, ViewStyle, Clipboard } from 'react-native'
import { ConfirmDialog } from '@trivie/ui-web/components/confirm-dialog'
import { DateTime } from 'luxon'
import { CommentProps } from './comment.props'
import { Text } from '../text'
import { spacing } from '../../theme/spacing'
import { color } from '../../theme/color'
import { Avatar } from '../avatar/avatar'
import { TextInputBubble } from '../text-input-bubble'
// @ts-ignore
import { ReactComponent as Heart } from '../post/heart.svg'
import { CommentReadModel, useStores } from '@trivie/core'
import { Instance } from 'mobx-state-tree'
import { AlertDialog } from '@trivie/ui-web/components/alert-dialog'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { SvgIcon } from '../svg-icon/svg-icon'
import { iconMoreHorizontal } from '../../assets'
import { ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, Divider } from '@mui/material'
import { ReportPostDialog } from '../../../web/components/report-post-modal/report-post-dialog'
import { useMediaQuery } from '@mui/material'
import { ReportSubmittedDialog } from '../../../web/components/report-submitted-dialog/report-submitted-dialog'

const STYLE: ViewStyle = {
  marginVertical: spacing[2],
  marginHorizontal: spacing[4],
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
}

const COMMENT_STYLE: ViewStyle = {
  borderRadius: 18,
  backgroundColor: color.palette.athensGrey,
  padding: 8,
  paddingLeft: 16,
}

export const Comment = observer((props: CommentProps) => {
  const { style, comment, children, isChildComment } = props
  const { userStore, talkStore } = useStores()
  const { user, getPermission } = userStore
  const {
    selectedPost,
    commentOnComment,
    likeComment,
    errorMessage,
    setErrorMessage,
    reportPost,
    deleteComment,
  } = talkStore
  const [showReply, setShowReply] = useState(false)
  const [selectedCommentId, setSelectedComment] = useState(0)

  const [editingComment] = React.useState<undefined | Instance<typeof CommentReadModel>>(undefined)
  const { t } = useTranslation()

  const [showContextToggle, setShowContextToggle] = useState(false)
  const [showOwnMenu, setShowOwnMenu] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [reportSubmitted, setReportSubmitted] = useState(false)

  const anchorRef = React.useRef<HTMLDivElement>(null)
  const reportRef = useRef<any>(null)

  const isMobile = !useMediaQuery('(min-width:800px)')

  useEffect(() => {
    setShowReply(false)
  }, [])

  const replyToComment = (c: any) => {
    if (user) {
      commentOnComment(c, selectedPost.id, selectedCommentId, user.id).then(() => {
        talkStore.refreshPost(selectedPost.id)
        setShowReply(false)
      })
    }
  }

  const likeChildComment = (c: any) => {
    likeComment(c)
  }

  const createdAt = DateTime.fromISO(comment.created_at, { zone: 'utc' })
  const now = DateTime.now()
  const elapsed = now.diff(createdAt).toMillis()

  const msPerMinute = 60 * 1000
  const msPerHour = msPerMinute * 60
  const msPerDay = msPerHour * 24
  const msPerMonth = msPerDay * 30
  const msPerYear = msPerDay * 365

  let elapsedTime = ''

  if (elapsed < msPerMinute) {
    elapsedTime = 'Just Now'
  } else if (elapsed < msPerHour) {
    elapsedTime = `${Math.round(elapsed / msPerMinute)}m`
  } else if (elapsed < msPerDay) {
    elapsedTime = `${Math.round(elapsed / msPerHour)}h`
  } else if (elapsed < msPerMonth) {
    elapsedTime = `${Math.round(elapsed / msPerDay)}d`
  } else if (elapsed < msPerYear) {
    elapsedTime = `${Math.round(elapsed / msPerMonth)}M`
  } else {
    elapsedTime = `${Math.round(elapsed / msPerYear)}y`
  }

  const renderAvatar = React.useMemo(() => {
    return (
      <Avatar
        size="small"
        style={{ marginTop: 4 }}
        source={comment.author.file_url}
        name={`${comment.author.first_name} ${comment.author.last_name}`}
      />
    )
  }, [`${comment.author.first_name} ${comment.author.last_name}`])
  const OtherAndOwnMenuToggle = () => {
    const userId = comment.author.id || comment.author.user_id
    if (userId === user?.id || getPermission('can_delete_post')) {
      setShowOwnMenu((prevState) => !prevState)
    }
  }

  const onReport = () => {
    setReportSubmitted(false)
    reportRef.current?.open()
  }

  const submitReportReason = (reportValue: any) => {
    if (reportValue) {
      reportPost(comment.id, reportValue)
    }
    setReportSubmitted(true)
  }
  const onCopyPostText = () => {
    Clipboard.setString(comment.text)
    setShowOwnMenu(false)
  }

  const handleMenuItemClick = (index: number) => {
    if (index === 1) {
      //@ts-ignore
      onComment(post)
    } else if (index === 3) {
      onCopyPostText()
    } else if (index === 4) {
      setShowConfirmDelete(true)
    } else if (index === 5) {
      setShowOwnMenu(false)
    } else if (index === 6) {
      setShowReportModal(true)
      onReport()
    }

    setShowOwnMenu(false)
  }

  useEffect(() => {
    if (!showOwnMenu) {
      setShowContextToggle(false)
    }
  }, [showOwnMenu])

  return (
    <>
      {comment && (
        <div
          onMouseEnter={() => setShowContextToggle(true)}
          onMouseLeave={() => {
            if (!showOwnMenu) {
              setShowContextToggle(showOwnMenu)
            }
          }}
        >
          <View style={{ ...STYLE, ...style }}>
            {renderAvatar}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, marginLeft: 8 }}>
              <div style={{ paddingRight: isMobile ? 12 : 24 }} role="textbox" aria-label="comment">
                <View style={COMMENT_STYLE}>
                  <View>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        position: 'relative',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <Text
                          variant="body2Emphasized"
                          text={
                            comment.author.first_name == null || comment.author.last_name == 'null'
                              ? 'Unnamed User'
                              : `${comment.author.first_name} ${comment.author.last_name}`
                          }
                        />
                        <Text style={{ color: color.shade50, alignSelf: 'center', bottom: 2 }}>
                          {' '}
                          ∙{' '}
                        </Text>
                        <Text
                          text={elapsedTime}
                          style={{ fontSize: 12, color: color.palette.rollingStone }}
                        />
                      </div>
                      <View style={{ position: 'absolute', right: -40, top: 4 }}>
                        {showContextToggle && (
                          <TouchableOpacity ref={anchorRef} onPress={() => OtherAndOwnMenuToggle()}>
                            <div aria-label="More Post Options">
                              <SvgIcon src={iconMoreHorizontal} color={color.black} size={24} />
                            </div>
                          </TouchableOpacity>
                        )}
                      </View>
                    </div>
                  </View>
                  <Text variant="body1" text={comment.text} />
                </View>
              </div>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginLeft: 16,
                  marginTop: spacing[1],
                }}
              >
                <TouchableOpacity
                  onPress={() => {
                    likeChildComment(comment)
                  }}
                  style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                >
                  <Heart
                    style={{
                      height: 15,
                      width: 15,
                      fontSize: 15,
                      fill: comment.liked_by_current_user
                        ? color.palette.red
                        : color.palette.rollingStone,
                    }}
                  />
                  <Text
                    variant="body2Emphasized"
                    text={comment.likes?.length || 'Like'}
                    style={{ marginLeft: spacing[1] }}
                  />
                </TouchableOpacity>
                {!isChildComment && (
                  <>
                    <TouchableOpacity
                      onPress={() => {
                        setShowReply(true)
                        setSelectedComment(comment.id)
                      }}
                    >
                      <Text
                        variant="body2Emphasized"
                        text="Reply"
                        style={{ marginLeft: spacing[3], color: color.palette.rollingStone }}
                      />
                    </TouchableOpacity>
                    <View style={{ flexDirection: 'column', paddingTop: 28 }}></View>
                  </>
                )}
              </View>
              {showReply ? (
                <>
                  <View
                    style={{
                      marginBottom: 30,
                      marginTop: 15,
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginLeft: -6,
                    }}
                  >
                    <Avatar
                      size="small"
                      style={{ marginRight: spacing[2], marginLeft: spacing[4] }}
                      source={user!.photo_url}
                      name={`${user?.first_name} ${user?.last_name}`}
                    />
                    <View style={{ flex: 1, flexDirection: 'row', paddingRight: 20 }}>
                      <TextInputBubble
                        style={{ width: '100%' }}
                        defaultValue={editingComment ? editingComment.text : ''}
                        placeholderTx="Discuss.writeReply"
                        onPost={replyToComment}
                      />
                    </View>
                  </View>
                </>
              ) : null}
            </div>
          </View>
        </div>
      )}
      <>
        <Popper
          open={showReportModal}
          style={{ marginLeft: 20, marginRight: 20, marginBottom: 66 }}
        />
        <ReportPostDialog
          isComment
          open={showReportModal}
          onSubmit={submitReportReason}
          onClose={() => setShowReportModal(false)}
        />
      </>
      <>
        <Popper
          open={reportSubmitted}
          style={{ marginLeft: 20, marginRight: 20, marginBottom: 66 }}
        />
        <ReportSubmittedDialog
          open={reportSubmitted}
          onSubmit={() => setReportSubmitted(false)}
          onClose={() => setReportSubmitted(false)}
        />
      </>
      {children}
      <AlertDialog
        open={!!errorMessage}
        title="Failed"
        message={t(errorMessage || '')}
        buttonText="Close"
        onClose={() => {
          setErrorMessage(null)
        }}
      />
      {/* OWN USER MENU */}
      <Popper
        open={showOwnMenu}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal={false}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            timeout={{ appear: 200, enter: 200, exit: 200 }}
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center left',
            }}
            hidden={!showOwnMenu}
          >
            <Paper style={DROPDOWN} variant="outlined">
              <ClickAwayListener onClickAway={() => setShowOwnMenu(false)}>
                <MenuList style={{ padding: 0 }} id="split-button-menu">
                  <MenuItem onClick={() => handleMenuItemClick(3)} style={MENU_ITEM_1}>
                    <Text tx="Discuss.copy" variant="body1Medium" style={{ fontSize: 16 }} />
                  </MenuItem>
                  <Divider />
                  {getPermission('can_delete_post') ? (
                    <MenuItem onClick={() => handleMenuItemClick(4)} style={MENU_ITEM_1}>
                      <Text tx="Discuss.delete" variant="body1Medium" style={{ fontSize: 16 }} />
                    </MenuItem>
                  ) : null}
                  <Divider />
                  <MenuItem onClick={() => handleMenuItemClick(6)} style={MENU_ITEM_1}>
                    <Text
                      tx="Discuss.reportHeader"
                      variant="body1Medium"
                      style={{ fontSize: 16 }}
                    />
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => handleMenuItemClick(5)} style={MENU_ITEM_1}>
                    <Text tx="Common.cancel" variant="body1Medium" style={{ fontSize: 16 }} />
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      <ConfirmDialog
        danger
        open={showConfirmDelete}
        title="Remove Comment"
        message="Are you sure you want to delete this comment?"
        confirmButton="Confirm"
        onConfirm={() => {
          deleteComment(comment).finally(() => {
            setShowConfirmDelete(false)
          })
        }}
        onClose={() => setShowConfirmDelete(false)}
        handleCancel={() => setShowConfirmDelete(false)}
      />
    </>
  )
})
const DROPDOWN: React.CSSProperties = {
  position: 'relative',

  top: -15,
  right: 40,
  width: 130,
  borderRadius: 10,
  padding: 0,
  boxShadow: '0px 4px 8px 8px rgba(0,0,0,0.03)',
}

const MENU_ITEM: React.CSSProperties = {
  padding: 0,
  paddingLeft: 18,

  height: 46,
  width: '100%',
  flexDirection: 'row',
}

const MENU_ITEM_1: React.CSSProperties = {
  ...MENU_ITEM,
}
const MENU_ITEM_3: React.CSSProperties = {
  ...MENU_ITEM,
  height: 60,
  borderBottomLeftRadius: 10,
  borderBottomRightRadius: 10,
}
